// Location utility functions for county vs town logic
// Used for SEO consolidation strategy

export interface LocationData {
  name: string;
  slug: string;
  county: string | null;
}

/**
 * Determines if a location is a county-level location
 * County: location.county === location.name OR location.county IS NULL
 * Town: location.county !== location.name AND location.county IS NOT NULL
 */
export const isCountyLocation = (location: { name: string; county: string | null }): boolean => {
  return !location.county || location.county === location.name;
};

/**
 * Gets the county slug for a given location
 * If it's already a county, returns its own slug
 * If it's a town, returns the parent county slug (requires county data)
 */
export const getCountySlugForLocation = (
  location: LocationData,
  allLocations?: LocationData[]
): string => {
  // If it's already a county, return its own slug
  if (isCountyLocation(location)) {
    return location.slug;
  }

  // If it's a town, find the parent county slug
  if (allLocations && location.county) {
    const countyLocation = allLocations.find(
      loc => loc.name === location.county && isCountyLocation(loc)
    );
    if (countyLocation) {
      return countyLocation.slug;
    }
  }

  // Fallback: slugify the county name if available
  return location.county 
    ? location.county.toLowerCase().replace(/\s+/g, '-')
    : location.slug;
};

/**
 * Gets all towns for a specific county
 */
export const getTownsForCounty = (
  countyName: string,
  allLocations: LocationData[]
): LocationData[] => {
  return allLocations.filter(
    loc => loc.county === countyName && !isCountyLocation(loc)
  );
};
